import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    LinearProgress,
    Snackbar,
    useMediaQuery,
    ThemeProvider,
    createTheme,
    Button,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import { styled } from '@mui/system';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
// Import components 
import CarrierSelection from './CarrierSelection';
import PlanSelection from './PlanSelection';
import DeviceCompatibility from './DeviceCompatibility';
import ShippingOptions from './ShippingOptions';
import TransferReviewAndConfirm from './TransferReviewAndConfirm';
import TransferPayment from './TransferPayment';

// Import services for API calls
import { carrierService } from '../../../../services/carrierService';
import { circleService } from '../../../../services/circleService';
import { paymentService } from '../../../../services/paymentService';
import { useAuth0 } from '@auth0/auth0-react';

// Import CSS
import '../../../ReservationForms/SwitchingCarriers.css';

// Enhanced Theme Configuration
const theme = createTheme({
    palette: {
        primary: {
            main: '#ff385c',
            light: '#ffeaee',
        },
        secondary: {
            main: '#00a699',
            light: '#e6f7f6',
        },
        info: {
            main: '#6366F1',
            light: '#eef0ff',
        },
        purple: {
            main: '#9333EA',
            light: '#f5ebff',
        },
        error: {
            main: '#ef4444',
            light: '#fee2e2',
        },
        warning: {
            main: '#f59e0b',
            light: '#fef3c7',
        },
        success: {
            main: '#10b981',
            light: '#d1fae5',
        },
        background: {
            default: '#f9fafb',
        },
        text: {
            primary: '#111827',
            secondary: '#6b7280',
        },
    },
    typography: {
        fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
        h5: {
            fontWeight: 700,
            fontSize: '1.5rem',
            lineHeight: 1.3,
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.125rem',
            lineHeight: 1.4,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.5,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.4,
        },
    },
    shape: {
        borderRadius: 12,
    },
    shadows: [
        'none',
        '0px 1px 2px rgba(0, 0, 0, 0.05)',
        '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
        '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
        '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
        '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    ],
});

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(10),
    maxWidth: '800px',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    backgroundColor: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
    },
}));

const ProgressIndicator = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
}));

const ColoredStepIcon = styled('div')(({ theme, ownerState }) => ({
    backgroundColor: ownerState.active ? theme.palette.primary.main : ownerState.completed ? theme.palette.success.main : '#E5E7EB',
    color: '#fff',
    width: 36,
    height: 36,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.3s ease-in-out',
    boxShadow: ownerState.active || ownerState.completed ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none',
}));

const StepConnector = styled(Box)(({ theme, active, completed }) => ({
    flex: 1,
    height: 2,
    backgroundColor: completed ? theme.palette.success.main : active ? theme.palette.primary.main : '#E5E7EB',
    transition: 'background-color 0.3s ease-in-out',
}));

const ActionButton = styled(Button)(({ theme, isback = false }) => ({
    borderRadius: '10px',
    padding: '12px 24px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: isback ? 'none' : theme.shadows[2],
    transition: 'all 0.2s ease-in-out',
    ...(isback
        ? {
            borderColor: '#6b7280',
            color: '#6b7280',
            '&:hover': {
                backgroundColor: '#F3F4F6',
                borderColor: '#4b5563',
            }
        }
        : {
            backgroundColor: '#0066ff',
            color: '#FFFFFF',
            '&:hover': {
                backgroundColor: '#0052cc',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-1px)',
            },
            '&:active': {
                transform: 'translateY(0)',
                boxShadow: theme.shadows[1],
            }
        }
    ),
}));

// Steps array for transfer flow - removing subscription selection
const steps = [
    'Choose New Carrier',
    'Select Plan',
    'Device Compatibility',
    'Shipping Options',
    'Review & Confirm',
    'Payment'
];

function ColoredStepIconComponent(props) {
    const { active, completed, className, icon } = props;
    return (
        <ColoredStepIcon ownerState={{ active, completed }} className={className}>
            {completed ? <Check size={20} /> : icon}
        </ColoredStepIcon>
    );
}

const CustomStepper = ({ activeStep, steps }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (isMobile) {
        return (
            <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepLabel StepIconComponent={ColoredStepIconComponent}>
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pb: 3 }}>
            {steps.map((label, index) => (
                <React.Fragment key={label}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ColoredStepIconComponent
                            active={activeStep === index}
                            completed={activeStep > index}
                            icon={index + 1}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 1,
                                color: activeStep === index ? 'primary.main' : activeStep > index ? 'success.main' : 'text.secondary',
                                fontWeight: activeStep === index ? 600 : 400,
                                fontSize: '0.75rem'
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                    {index < steps.length - 1 && (
                        <StepConnector
                            active={activeStep > index}
                            completed={activeStep > index + 1}
                        />
                    )}
                </React.Fragment>
            ))}
        </Box>
    );
};

const TransferSwitchingCarriers = () => {
    // Hooks
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { getAccessTokenSilently } = useAuth0();

    // State
    const [initialized, setInitialized] = useState(false);
    const [validTransfer, setValidTransfer] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedCarrier, setSelectedCarrier] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [lineCount, setLineCount] = useState(1);
    const [imeis, setImeis] = useState([]);
    const [esimOptions, setEsimOptions] = useState([]);
    const [imei2Values, setImei2Values] = useState([]);
    const [imeiValidation, setImeiValidation] = useState([]);
    const [shippingMethod, setShippingMethod] = useState('standard');
    const [zipCode, setZipCode] = useState('');
    const [areaCode, setAreaCode] = useState('');
    const [progress, setProgress] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [reviewData, setReviewData] = useState(null);
    const [circles, setCircles] = useState([]);
    const [existingSubscription, setExistingSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Initialize component and handle redirects if needed
    useEffect(() => {
        const subscription = location.state?.existingSubscription || null;
        const transferMode = location.state?.isTransfer || false;

        setExistingSubscription(subscription);

        if (subscription && transferMode) {
            const lines = subscription.lines || 1;
            setLineCount(lines);

            // Initialize arrays based on line count
            setImeis(Array(lines).fill(''));
            setEsimOptions(Array(lines).fill('no'));
            setImei2Values(Array(lines).fill(''));
            setImeiValidation(Array(lines).fill(true));

            setValidTransfer(true);
        } else {
            // Redirect if not a valid transfer
            navigate('/switch-carrier', { replace: true });
        }

        setInitialized(true);
        setIsLoading(false);
    }, [location, navigate]);

    // Update progress bar when step changes
    useEffect(() => {
        setProgress((activeStep / (steps.length - 1)) * 100);
    }, [activeStep]);

    // Fetch available circles based on selected carrier using service
    useEffect(() => {
        if (selectedCarrier) {
            setIsLoading(true);
            
            const fetchCircles = async () => {
                try {
                    // Get auth token
                    const token = await getAccessTokenSilently({
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                        scope: 'openid profile email'
                    });
                    
                    // Use the carrier service to fetch circles by carrier
                    const response = await circleService.getCirclesByCarrier(selectedCarrier.toLowerCase(), token);
                    setCircles(response.data || []);
                } catch (error) {
                    console.error('Error fetching circles:', error);
                    setSnackbarMessage(error.friendlyMessage || 'Failed to load available circles.');
                    setSnackbarOpen(true);
                    // Fallback to empty circles array on error
                    setCircles([]);
                } finally {
                    setIsLoading(false);
                }
            };
            
            fetchCircles();
        }
    }, [selectedCarrier, getAccessTokenSilently]);

    const validateIMEI = (imei) => {
        return /^\d{15}$/.test(imei);
    };

    const validateAllIMEIs = () => {
        // At least one IMEI should be valid or all can be empty (making them optional)
        return imeis.some(imei => validateIMEI(imei)) || imeis.every(imei => imei === '');
    };

    const validateForm = () => {
        switch (activeStep) {
            case 0:
                return !!selectedCarrier;
            case 1:
                return !!selectedPlan;
            case 2:
                return validateAllIMEIs();
            case 3:
                if (esimOptions.every(option => option === 'yes')) {
                    return true;
                }
                return !!shippingMethod;
            case 4:
            case 5:
                return true;
            default:
                return false;
        }
    };

    // Handler functions for IMEI fields
    const handleIMEIChange = (index, value) => {
        const newImeis = [...imeis];
        newImeis[index] = value;
        setImeis(newImeis);

        const newValidation = [...imeiValidation];
        newValidation[index] = validateIMEI(value) || value === '';
        setImeiValidation(newValidation);
    };

    const handleEsimChange = (index, value) => {
        const newEsimOptions = [...esimOptions];
        newEsimOptions[index] = value;
        setEsimOptions(newEsimOptions);
    };

    const handleImei2Change = (index, value) => {
        const newImei2Values = [...imei2Values];
        newImei2Values[index] = value;
        setImei2Values(newImei2Values);
    };

    const handleNext = () => {
        if (!validateForm()) {
            setSnackbarMessage('Please fill in all required fields correctly.');
            setSnackbarOpen(true);
            return;
        }

        if (activeStep === steps.length - 3) {
            // Calculate the correct tier price based on selected plan
            const perLineCost = selectedPlan ? selectedPlan.pricePerLine : 0;

            // Line details
            const lineDetails = imeis.map((imeiValue, index) => ({
                imei: imeiValue,
                esim: esimOptions[index] === 'yes',
                imei2: esimOptions[index] === 'yes' ? imei2Values[index] : '',
                lineNumber: index + 1
            }));

            // Calculate activation fee
            const perLineActivationFee = 35.00;
            const totalActivationFee = perLineActivationFee * lineCount;

            // Calculate monthly cost
            const monthlyRate = perLineCost * lineCount;
            const taxes = 5.00 * lineCount;

            // Prepare review data
            const reviewData = {
                planDetails: {
                    circleName: selectedPlan?.circleName || selectedCarrier,
                    planName: selectedPlan?.name || `${selectedCarrier} Plan`,
                    planId: selectedPlan?.id,
                    lines: lineCount,
                    monthlyRate: monthlyRate,
                    taxes: taxes,
                    totalMonthly: monthlyRate + taxes,
                    activationFeePerLine: perLineActivationFee,
                    totalActivationFee: totalActivationFee,
                    carrier: selectedCarrier.toLowerCase(),
                    originalSubscription: existingSubscription
                },
                userDetails: {
                    numLines: lineCount,
                    lineDetails: lineDetails,
                    hasEsim: esimOptions.some(option => option === 'yes'),
                    area_code: areaCode || '',
                    zip_code: zipCode || '',
                    circle_id: selectedPlan?.circleId,
                    circle_name: selectedPlan?.circleName || selectedCarrier
                },
                subscriptionDetails: {
                    type: existingSubscription?.subscription_type || 'monthly',
                    shipping: esimOptions.every(option => option === 'yes') ? 'eSIM' : shippingMethod,
                    isTransfer: true,
                    originalSubscriptionId: existingSubscription?.id
                }
            };

            setReviewData(reviewData);
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleCancel = () => {
        navigate('/my-profile');
    };

    // Function to create a transfer payment using payment service
    const createTransferPayment = async (paymentMethodId) => {
        try {
            setIsLoading(true);
            
            // Get auth token
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            // Create payment data
            const paymentData = {
                payment_method_id: paymentMethodId,
                transfer_data: reviewData,
                set_as_default: true
            };
            
            // Use payment service to create transfer payment
            const response = await paymentService.createTransferPayment(paymentData, token);
            
            return response.data;
        } catch (error) {
            console.error('Payment error:', error);
            setSnackbarMessage(error.friendlyMessage || 'Payment failed. Please try again.');
            setSnackbarOpen(true);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0: // Choose New Carrier
                return (
                    <CarrierSelection
                        selectedCarrier={selectedCarrier}
                        onSelectCarrier={(carrier) => setSelectedCarrier(carrier)}
                        existingSubscription={existingSubscription}
                    />
                );

            case 1: // Select Plan
                return (
                    <PlanSelection
                        selectedCarrier={selectedCarrier}
                        circles={circles}
                        onSelectPlan={setSelectedPlan}
                        selectedPlan={selectedPlan}
                        isLoading={isLoading}
                    />
                );

            case 2: // Device Compatibility
                return (
                    <DeviceCompatibility
                        lineCount={lineCount}
                        imeis={imeis}
                        esimOptions={esimOptions}
                        imei2Values={imei2Values}
                        imeiValidation={imeiValidation}
                        onImeiChange={handleIMEIChange}
                        onEsimChange={handleEsimChange}
                        onImei2Change={handleImei2Change}
                        selectedCarrier={selectedCarrier}
                    />
                );

            case 3: // Shipping Options
                return (
                    <ShippingOptions
                        esimOptions={esimOptions}
                        shippingMethod={shippingMethod}
                        setShippingMethod={setShippingMethod}
                        existingSubscription={existingSubscription}
                    />
                );

            case 4: // Review & Confirm
                return (
                    <TransferReviewAndConfirm
                        planDetails={reviewData?.planDetails}
                        userDetails={reviewData?.userDetails}
                        subscriptionDetails={reviewData?.subscriptionDetails}
                        onEdit={handleBack}
                    />
                );

            case 5: // Payment
                return (
                    <TransferPayment
                        reviewData={reviewData}
                        onBack={handleBack}
                        createPayment={createTransferPayment}
                        isLoading={isLoading}
                        onPaymentSuccess={(paymentResult) => {
                            navigate('/success', {
                                state: {
                                    ...reviewData,
                                    paymentResult,
                                    isTransfer: true
                                }
                            });
                        }}
                    />
                );

            default:
                return null;
        }
    };

    // Show loading indicator while initializing
    if (!initialized) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                <CircularProgress size={40} color="primary" />
            </Box>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <div className="switching-carrier-wrapper">
                <StyledContainer>
                    <ProgressIndicator mb={4}>
                        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 1 }}>
                            Estimated time to complete: 5 minutes
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: '#E5E7EB',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    background: 'linear-gradient(90deg, #ff385c 0%, #ff5c83 100%)',
                                }
                            }}
                        />
                    </ProgressIndicator>

                    <StyledPaper sx={{ mt: 5 }}>
                        <form onSubmit={(e) => e.preventDefault()}>
                            <CustomStepper activeStep={activeStep} steps={steps} />

                            <Box sx={{ px: { xs: 1, sm: 3 }, py: 3 }}>
                                {isLoading && activeStep !== 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                                        <CircularProgress size={40} color="primary" />
                                    </Box>
                                ) : (
                                    renderStepContent(activeStep)
                                )}
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mt: 4,
                                    flexDirection: isMobile ? 'column-reverse' : 'row',
                                    gap: 2
                                }}
                            >
                                {activeStep === 0 ? (
                                    <ActionButton
                                        variant="outlined"
                                        onClick={handleCancel}
                                        startIcon={<ArrowLeft size={18} />}
                                        isback="true"
                                        fullWidth={isMobile}
                                    >
                                        Cancel
                                    </ActionButton>
                                ) : (
                                    <ActionButton
                                        variant="outlined"
                                        onClick={handleBack}
                                        startIcon={<ArrowLeft size={18} />}
                                        isback="true"
                                        fullWidth={isMobile}
                                    >
                                        Back
                                    </ActionButton>
                                )}

                                {activeStep !== steps.length - 1 && (
                                    <ActionButton
                                        variant="contained"
                                        onClick={handleNext}
                                        endIcon={<ArrowRight size={18} />}
                                        fullWidth={isMobile}
                                        disabled={isLoading}
                                    >
                                        {activeStep === steps.length - 2 ? 'Proceed to Payment' : 'Next'}
                                    </ActionButton>
                                )}
                            </Box>
                        </form>
                    </StyledPaper>

                    <Snackbar
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        open={snackbarOpen}
                        autoHideDuration={3000}
                        onClose={() => setSnackbarOpen(false)}
                        message={snackbarMessage}
                    />
                </StyledContainer>
            </div>
        </ThemeProvider>
    );
};

export default TransferSwitchingCarriers;